/* eslint-disable react/jsx-indent */
import React, {
  useState, useEffect, useMemo, Dispatch, useContext, ChangeEvent,
} from 'react';
import {
  Chip,
  FormControl,
  Input,
  InputLabel,
  Button,
  Box,
  CircularProgress,
  TextField,
  Typography,
  FormHelperText,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import PersonIcon from '@mui/icons-material/Person';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import { useImmer } from 'use-immer';
import { DICTIONARY } from 'shared/constants';
import ActivityService from 'services/activity';
import CompanyService from 'services/company';
import PersonService from 'services/person';
import AuthService, { MH_ROLES } from 'services/auth';
import {
  ActivityType,
  ArchivedState,
  Company,
  CompanyType,
  CreateCompanyInput,
  Employer,
  PersonType,
  PopulateCreateCompanyFormQuery,
  Role,
  TeamMember,
  UpdateCompanyInput,
} from 'API';
import { populateCreateCompanyForm } from 'graphql/customQueries';
import AutocompleteWithSearch from 'components/forms/AutocompleteWithSearch';
import { ChangeValueType } from 'shared/propTypes';
import {
  alphaByName, emailIsInWhitelist, isValidEmail, notNullFilter,
} from 'shared/utilities';
import { ROUTES } from 'shared/routes';
import { useNavigate } from 'react-router-dom';
import Dropdown from 'components/Dropdown';
import IconCircle from 'components/IconCircle';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import S3Avatar from 'components/S3Avatar';
import CompanyContext from 'shared/contexts/CompanyContext';
import { FIRM_COMPANY } from 'services/firm';
import UserContext from 'shared/contexts/UserContext';
import { createPrimaryContact } from 'graphql/mutations';
import AlertContext from 'shared/contexts/AlertContext';
import SidebarContext from 'shared/contexts/SidebarContext';
import Toggle from './Toggle';
import StringArray from './StringArray';
import { client } from 'lib/graphql-client';
import { GraphQLResult } from '@aws-amplify/api';

const {
  assignAttorney,
  assignParalegal,
  assignParent,
  unassignAttorney,
  unassignParalegal,
  unassignParent,
} = CompanyService;
const { fullname } = PersonService;

/**
 * Collects connection writes that failed, without aborting the save.
 *
 * These are settled rather than all-or-nothing on purpose: the writes must be
 * waited for (otherwise the refetch that follows reads stale connections), but
 * `unassignParent`/`unassignAttorney`/`unassignParalegal` reject on any error, and
 * with `Promise.all` one stale relationship would throw before the company's own
 * fields were ever saved. The caller alerts on whatever is returned here — a
 * partial save must not look like a clean one.
 */
const connectionFailures = (results: Array<PromiseSettledResult<unknown>>) => {
  const failures = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected',
  );

  if (failures.length) {
    // eslint-disable-next-line no-console
    console.error(
      'Some company connection updates failed',
      failures.map((failure) => failure.reason),
    );
  }

  return failures;
};

interface PrimaryContact {
  companyID?: string;
  email: string; // As entered by user
  employer: Employer | null; // Retrieved from graphql, if present
  error: string | null;
}

interface CheckboxOption {
  selected: boolean;
  option: TeamMember | Company;
}

const initialCompanyInputState = {
  name: '',
  isParent: false,
  isPartnerCompany: false,
  slug: '',
  companyInputId: '',
  type: CompanyType.Company,
  lowerNameKey: '',
  companyNumber: 0,
  nationalCount: 0,
};

const initialPrimaryContacts = [{
  email: '', employer: null, error: null, companyID: '',
}];

interface Props {
  isEdit?: boolean;
  setOpen: Dispatch<boolean>;
}

const CompanyForm: React.FC<Props> = ({ isEdit, setOpen }: Props) => {
  const [company, setCompany] = useContext(CompanyContext);
  const navigate = useNavigate();
  const [userState] = useContext(UserContext);
  const { person, role } = userState;
  const [disabledButton, setDisabledButton] = useState(true);
  const [companyInput, setCompanyInput] = useState<
  CreateCompanyInput | UpdateCompanyInput
  >(
    isEdit
      ? {
        id: company!.id || '',
        name: company!.name,
        domainWhitelist: company!.domainWhitelist,
        isParent: company!.isParent,
        accountManagerID: company!.accountManagerID,
        archived: company!.archived,
        lowerNameKey: company!.lowerNameKey,
      }
      : initialCompanyInputState,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [, setAlert] = useContext(AlertContext);
  const [
    orphanCompanies, setOrphanCompanies,
  ] = useImmer<Array<CheckboxOption>>(
    // Defensive: `company` is already null in create mode (CreateSpeedDial
    // renders from Header, a sibling of the routed subtrees that provide
    // CompanyContext), so this guard changes nothing today. It states the
    // requirement explicitly in case the form is ever mounted under a provider.
    isEdit
      ? (company?.children?.items?.filter(notNullFilter) || [])
        .map((child) => ({ selected: true, option: child as Company }))
      : [],
  );
  const [
    accountManager, setAccountManager,
  ] = useState<TeamMember | null>(
    (isEdit && company!.accountManager)
      ? company!.accountManager
      : null,
  );
  const [attorneys, setAttorneys] = useImmer<Array<TeamMember>>([]);
  const [selectedAttorneys, setSelectedAttorneys] = useState<Array<TeamMember>>(
    (company?.assignedAttorneys?.items?.map((att) => ({ ...att?.attorney }))
    || []) as TeamMember[],
  );
  const [paralegals, setParalegals] = useImmer<Array<TeamMember>>([]);
  const [selectedParalegals, setSelectedParalegals] = useState<Array<TeamMember>>(
    (company?.assignedParalegals?.items?.map((para) => ({ ...para?.paralegal }))
    || []) as TeamMember[],
  );
  const [team, setTeam] = useState<Array<TeamMember>>([]);
  const [
    primaryContacts, setPrimaryContacts,
  ] = useImmer<Array<PrimaryContact>>(initialPrimaryContacts);
  const [isCheckingPrimaryContact, setIsCheckingPrimaryContact] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [sendInvite, setSendInvite] = useState(false);
  const { setNewItem, setUpdatedItem } = useContext(SidebarContext);

  // The children already saved against this company. They still carry a
  // parentID until the save runs, so without them the eligibility check below
  // would reject a chip the user just deleted and block re-adding it.
  const savedChildIDs = useMemo(
    () => (company?.children?.items?.filter(notNullFilter) || []).map((child) => child!.id),
    [company],
  );

  /**
   * A company can be offered as a subsidiary only if it is an orphan — not
   * already someone's child, and not itself a parent (subsidiaries do not nest).
   * The search query cannot express "has no parentID", so results are narrowed
   * here. This company's own saved children are always eligible, so removing a
   * chip and changing your mind does not require cancelling the modal.
   */
  const isEligibleSubsidiary = (option: Company) => savedChildIDs.includes(option.id)
    || (!option.isParent && !option.parentID);

  // Memoised because MUI's useAutocomplete compares `value` by reference: a new
  // array each render reads as a value change, which bypasses its `focused`
  // guard and resets the input — wiping whatever the user was typing.
  const selectedSubsidiaries = useMemo(
    () => orphanCompanies
      .filter((orphan) => orphan.selected)
      .map((orphan) => orphan.option as Company),
    [orphanCompanies],
  );

  const canSeeCompanyTeam = MH_ROLES.includes(role);
  const canSeeDomains = MH_ROLES.includes(role);
  const canSeeCompanyPreferences = MH_ROLES.includes(role);

  const checkRequiredFields = () => {
    if (companyInput.isPartnerCompany) {
      // Partner companies require only a name
      if (companyInput.name && companyInput.name.length > 0) {
        setDisabledButton(false);
      } else {
        setDisabledButton(true);
      }
    } else if (
      // New client companies require attorney and primary contact
      (companyInput.name
        && companyInput.name.length > 0
        && companyInput.domainWhitelist
        && selectedAttorneys.length > 0
        && primaryContacts.length > 0
        && accountManager
        && isValidEmail(emailInput)
        && !primaryContacts.some((contact) => !isValidEmail(contact.email)))
      // Client updates require only name and attorney
      || (isEdit
        && companyInput.name
        && companyInput.name.length > 0
        && companyInput.domainWhitelist
        && accountManager
        && selectedAttorneys.length > 0)
    ) {
      setDisabledButton(false);
    } else {
      setDisabledButton(true);
    }
  };

  const handleChange = (eventTarget: any) => {
    setCompanyInput({ ...companyInput, [eventTarget.name]: eventTarget.value });
  };

  const handleDelete = () => {
    setEmailInput('');
  };

  const createEmployer = async (contact: PrimaryContact) => {
    const newPerson = await PersonService.create(
      {
        email: contact.email,
        slug: '',
        type: PersonType.Employer,
        archived: ArchivedState.ACTIVE,
        lowerFirst: '-',
        lowerLast: '-',
        lowerFull: '-',
        companyID: contact.companyID,
      },
      Role.EMPLOYER,
    );

    return newPerson;
  };

  /**
   * Take the current list of primary contacts and create
   * new employers for any email addresses that aren't in the
   * system yet. Returns a list of all employers, new and old.
   * @returns {Array<Employer>}
   */
  const createEmployers = async (
    companyID: string,
  ) => Promise.all(primaryContacts
    .filter((contact) => isValidEmail(contact.email))
    .filter((contact) => !contact.error)
    .map(
      async (contact) => contact.employer || createEmployer({ ...contact, companyID }),
    ));

  const inviteEmployers = async (companyID: string, employers: Array<Employer>) => {
    await Promise.all(
      employers
        .filter((employer) => employer.email && isValidEmail(employer.email))
        .filter((employer) => !employer.username)
        .map(async (employer) => AuthService.createUser({
          companyAccess: [companyID],
          email: employer.email!,
          role: Role.EMPLOYER,
          personID: employer.id!,
        })),
    );
  };

  const reset = (draft: Array<CheckboxOption>) => {
    draft.forEach((option, i) => {
      draft[i] = {
        selected: false,
        option: option.option,
      };
    });
  };

  const resetCheckboxes = () => {
    setSelectedAttorneys([]);
    setSelectedParalegals([]);
    setOrphanCompanies(reset);
  };

  const handleAccountManagerChange = (selection: any) => {
    const newManager = team.find((member) => member.id === selection.id);
    setAccountManager(newManager || null);
    setCompanyInput({
      ...companyInput,
      accountManagerID: newManager ? newManager.id : null,
    });
  };

  const handleReset = () => {
    setCompanyInput(initialCompanyInputState);
    setPrimaryContacts(initialPrimaryContacts);
    setEmailInput('');
    resetCheckboxes();
  };

  const handleSubsidiaryChange = async (
    _event: any, selections: ChangeValueType,
  ) => {
    setOrphanCompanies(
      (selections as Array<Company>).map((option) => ({ selected: true, option })),
    );
  };

  const handleWhiteListChange = (
    e: ChangeEvent<HTMLInputElement>, strings: Array<string>,
  ) => {
    setCompanyInput({
      ...companyInput,
      domainWhitelist: strings,
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);

    if (isEdit) {
      try {
        setIsLoading(true);

        const selectedAttorneyIDs = selectedAttorneys
          .map((attorney) => attorney.id);

        const selectedParalegalIDs = selectedParalegals
          .map((paralegal) => paralegal.id);

        const selectedChildrenIDs = orphanCompanies
          .filter((comp) => comp.selected)
          .map((comp) => comp.option.id);

        const removedAttorneyIDs = company?.assignedAttorneys?.items?.filter(
          (item) => item?.id && !selectedAttorneyIDs.includes(item?.attorney?.id),
        ).map((item) => item?.id) || [];

        const removedParalegalIDs = company?.assignedParalegals?.items
          ?.filter(
            (item) => item?.id && !selectedParalegalIDs.includes(item?.paralegal?.id),
          )
          .map((item) => item?.id) || [];

        const removedChildrenIDs = company?.children?.items
          ?.filter(
            (item) => item?.id && !selectedChildrenIDs.includes(item?.id),
          )
          .map((item) => item?.id) || [];

        // If company is no longer a parent, remove its children
        if (!companyInput.isParent) {
          removedChildrenIDs
            .push(...(company?.children?.items?.filter(notNullFilter).map(
              (child) => child?.id,
            ) || []));
        }

        // A child the filter above already queued gets pushed a SECOND time by
        // that block (it is never in selectedChildrenIDs once the toggle is
        // off), which fired two concurrent unassignParent writes for the same
        // row. unassignParent rejects on conflict, so the losing write surfaced
        // as a spurious "some changes could not be applied" alert on a save
        // that had actually worked.
        const childrenToUnassign = [...new Set(removedChildrenIDs)];

        // First, update the connections.
        // Each callback must RETURN its promise. With a block body it evaluated
        // to undefined, so this resolved immediately and the refetch below read
        // the connections before these writes had landed — the saved
        // subsidiaries, attorneys and paralegals only appeared after a manual
        // page refresh.
        const failures = connectionFailures(await Promise.allSettled([
          ...selectedAttorneys
            .map((attorney) => assignAttorney(company!.id!, attorney.id!)),
          ...removedAttorneyIDs.map(
            (relationshipID) => unassignAttorney(relationshipID!),
          ),
          ...selectedParalegals
            .map((paralegal) => assignParalegal(company!.id!, paralegal.id!)),
          ...removedParalegalIDs.map(
            (relationshipID) => unassignParalegal(relationshipID!),
          ),
          // Guarded on isParent: turning the toggle off queues every child for
          // unassign above while orphanCompanies keeps its now-hidden
          // selections, so without this both run in the same batch and
          // assignParent's read-then-write can clobber the unassign.
          ...(companyInput.isParent
            ? orphanCompanies
              .filter((comp) => comp.selected)
              .map((comp) => assignParent(comp.option.id!, company!.id!))
            : []),
          ...childrenToUnassign
            .map((child) => unassignParent(child!)),
        ]));

        if (failures.length) {
          setAlert([DICTIONARY.ConnectionUpdateError, 'error']);
        }

        // Second, update the company
        const updatedCompany = await CompanyService.update(
          companyInput as UpdateCompanyInput,
          setCompany,
        );

        if (companyInput.accountManagerID
          && (companyInput.accountManagerID !== company?.accountManagerID)) {
          ActivityService.createActivity({
            activityType: ActivityType.LEGAL_TEAM_ACCOUNT_MANAGER_ADDED,
            actorID: person.id!,
            actorType: person.type,
            directTargetID: companyInput.accountManagerID!,
            indirectTargetID: companyInput.id!,
          }, [
            companyInput.accountManagerID,
          ]);
        }

        setOpen(false);
        setUpdatedItem(updatedCompany);
      } catch (err: any) {
        setAlert([err.message || DICTIONARY.DefaultError, 'error']);
        setIsLoading(true);
      }
    } else {
      const slug = await CompanyService.generateSlug(companyInput.name!);

      try {
        const newCompany = await CompanyService.create({
          isParent: companyInput.isParent,
          isPartnerCompany: companyInput.isPartnerCompany,
          name: companyInput.name!,
          domainWhitelist: companyInput.domainWhitelist,
          slug,
          accountManagerID: accountManager?.id,
          companyDataId: '',
          type: CompanyType.Company,
          lowerNameKey: '',
          companyNumber: 0,
          nationalCount: 0,
        });

        if (newCompany?.name) {
          const isPartner = newCompany.isPartnerCompany;

          // Now that we have a company, let's get a list of its primary
          // contacts, ensuring that each has a corresponding Employer.
          // If not, create one.
          const employers: Array<Employer> = isPartner
            ? [] : await createEmployers(newCompany.id);

          if (sendInvite) {
            await inviteEmployers(newCompany.id, employers);
          }

          // Once we have that list, create relationships between the
          // new company and the appropriate employers, attorneys, and
          // paralegals. Assign parents to any child companies, too.
          // Don't do this for partner companies, however.

          const connectionResults = connectionFailures(await Promise.allSettled([
            ...employers.map(async (employer) => {
              try {
                await Promise.all([
                  client.graphql({
                    query: createPrimaryContact,
                    variables: {
                      input: {
                        companyID: newCompany.id,
                        employerID: employer.id,
                      },
                    },
                  }),
                  CompanyService.updateEmployerAssignments(
                    employer.id!, [newCompany.id], [],
                  ),
                ]);
              } catch (err: any) {
                setAlert([err.message || DICTIONARY.DefaultError, 'error']);
              }
            }),
            // Returned, not fire-and-forget — see the note on the edit path's
            // Promise.all above.
            ...(isPartner ? [] : selectedAttorneys)
              .map((attorney) => CompanyService.assignAttorney(
                newCompany.id,
                attorney.id!,
              )),
            ...(isPartner ? [] : selectedParalegals)
              .map((paralegal) => CompanyService.assignParalegal(
                newCompany.id,
                paralegal.id!,
              )),
            // Partner companies never become anyone's parent, and neither does a
            // company saved with the parent toggle off — the field hides but
            // orphanCompanies keeps whatever was picked before it was hidden.
            ...(newCompany.isPartnerCompany || !newCompany.isParent
              ? []
              : orphanCompanies
                .filter((comp) => comp.selected)
                .map((comp) => CompanyService.assignParent(
                  comp.option.id!, newCompany.id,
                ))),
          ]));

          if (connectionResults.length) {
            setAlert([DICTIONARY.ConnectionUpdateError, 'error']);
          }

          if (newCompany.accountManagerID) {
            await ActivityService.createActivity({
              activityType: ActivityType.LEGAL_TEAM_ACCOUNT_MANAGER_ADDED,
              actorID: person.id!,
              actorType: person.type,
              directTargetID: newCompany.accountManagerID!,
              indirectTargetID: newCompany.id!,
            }, [
              newCompany.accountManagerID,
            ]);
          }

          setOpen(false);
          setNewItem(newCompany);
          navigate(`${ROUTES.companies}/${newCompany.slug}`);
        } else {
          throw new Error('An error occured creating this company.');
        }
      } catch (err: any) {
        setAlert([err.message || DICTIONARY.DefaultError, 'error']);
        setOpen(false);
      }
    }
  };

  const handlePrimaryContactInput = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.target;
    // Kind of an ugly way to determine which
    // primary contact we're updating here.
    const primaryContactIndex = parseInt(
      e.target.id.replace('primary-contact-input-', ''), 10,
    );

    checkRequiredFields();

    setPrimaryContacts((draft) => {
      draft[primaryContactIndex] = {
        email: value,
        employer: null,
        error: null,

      };
    });
    if (isValidEmail(value)) {
      setEmailInput(value);

      if (!emailIsInWhitelist(value, companyInput.domainWhitelist || [])) {
        setPrimaryContacts((draft) => {
          draft[primaryContactIndex].error = DICTIONARY.WhitelistMismatchError;
        });
      } else {
        setIsCheckingPrimaryContact(true);
        const newPerson = await PersonService.getByEmail(value);

        if (newPerson && newPerson.type === PersonType.Employer) {
          setPrimaryContacts((draft) => {
            draft[primaryContactIndex].employer = newPerson;
          });
        } else if (newPerson) {
          setPrimaryContacts((draft) => {
            draft[primaryContactIndex].error = DICTIONARY.NonEmployerError;
          });
        }

        setIsCheckingPrimaryContact(false);
      }
    }
  };

  useEffect(() => {
    checkRequiredFields();
  }, [companyInput, selectedAttorneys, primaryContacts, emailInput]);

  useEffect(() => {
    if (!companyInput.isParent) {
      setOrphanCompanies(reset);
    }
  }, [companyInput.isParent]);

  useEffect(() => {
    let ignore = false;

    const initializeForm = async () => {
      try {
        const response = ((await client.graphql({
          query: populateCreateCompanyForm,
        })) as GraphQLResult<PopulateCreateCompanyFormQuery>).data;

        if (response && !ignore) {
          setAttorneys(
            ((response.attorneys?.items || []) as Array<TeamMember>)
              .map(notNullFilter)
              .sort(alphaByName),
          );
          setParalegals(
            ((response.paralegals?.items || []) as Array<TeamMember>)
              .map(notNullFilter)
              .sort(alphaByName),
          );
          setTeam([
            ...(response.attorneys?.items || []) as Array<TeamMember>,
            ...(response.paralegals?.items || []) as Array<TeamMember>,
          ].sort(alphaByName));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    };

    initializeForm();

    return () => {
      ignore = true;
    };
  }, []);

  return isLoading ? (
    <CircularProgress sx={{ marginTop: '2rem' }} />
  ) : (
    <form onSubmit={(e) => handleSubmit(e)} id="create-company-form">
      <IconRow isInput>
        <IconRowCell type="icon">
          <BusinessIcon fontSize="small" />
        </IconRowCell>
        <IconRowCell type="text">
          <FormControl required sx={{ marginBottom: '1rem', width: '100%' }}>
            <InputLabel htmlFor="company-name-input">
              {DICTIONARY.LegalNameOfUSCompany}
            </InputLabel>
            <Input
              id="company-name-input"
              name="name"
              onChange={(e) => handleChange(e.target)}
              value={companyInput.name}
            />
          </FormControl>
        </IconRowCell>
      </IconRow>

      {!companyInput.isPartnerCompany && canSeeCompanyTeam && (
        <Box component="section" sx={{ mb: 4 }}>
          <IconRow sx={{ marginBottom: '1rem', width: '100%' }}>
            <IconRowCell type="icon">
              <IconCircle Icon={PersonIcon} />
            </IconRowCell>
            <IconRowCell type="text">
              <Typography component="h3" variant="h5">
                {`Assigned ${FIRM_COMPANY.name} Team`}
              </Typography>
            </IconRowCell>
          </IconRow>

          <IconRow isInput sx={{ marginBottom: '1rem', width: '100%' }}>
            <IconRowCell type="icon">
              <PersonIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <Autocomplete
                id="attorney-list"
                className="autocomplete--chips"
                multiple
                options={attorneys}
                getOptionLabel={(option) => `${fullname(option)}`}
                onChange={(event, value) => setSelectedAttorneys(value)}
                value={selectedAttorneys}
                isOptionEqualToValue={
                  (option, value) => option.id === value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label={selectedAttorneys.length === 0 ? 'Attorneys' : 'Attorneys *'}
                    required={selectedAttorneys.length === 0}
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'selectedAttorneys',
                    }}
                  />
                )}
              />
            </IconRowCell>
          </IconRow>

          <IconRow isInput sx={{ marginBottom: '1rem', width: '100%' }}>
            <IconRowCell type="icon">
              <PersonIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <Autocomplete
                id="paralegal-list"
                className="autocomplete--chips"
                multiple
                options={paralegals}
                getOptionLabel={(option) => `${fullname(option)}`}
                onChange={(event, value) => setSelectedParalegals(value)}
                value={selectedParalegals}
                isOptionEqualToValue={
                  (option, value) => option.id === value.id
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="Paralegals"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'paralegals',
                    }}
                  />
                )}
              />
            </IconRowCell>
          </IconRow>

          <IconRow isInput>
            <IconRowCell type="icon">
              <PersonIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <Dropdown
                id="account-manager-list"
                canEdit
                fullWidth
                label="Account Manager"
                noEmployerLabel
                items={team}
                selectedItem={accountManager}
                onChange={handleAccountManagerChange}
                required
              />
            </IconRowCell>
          </IconRow>
        </Box>
      )}

      {!companyInput.isPartnerCompany && canSeeDomains && (
        <Box component="section" sx={{ mb: 4 }}>
          <IconRow isHeader>
            <IconRowCell type="icon">
              <IconCircle Icon={PersonIcon} />
            </IconRowCell>
            <IconRowCell type="text">
              <Typography component="h3" variant="h5">
                  Company Email Contact
              </Typography>
            </IconRowCell>
          </IconRow>

          <IconRow isInput>
            <IconRowCell type="icon">
              <AlternateEmailIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <FormControl required sx={{ marginBottom: '1rem', width: '100%' }}>
                <InputLabel htmlFor="whitelist-input" shrink>
                  {DICTIONARY.DomainWhitelist}
                </InputLabel>
                <StringArray
                  value={companyInput.domainWhitelist}
                  name="allowed-domains"
                  id="whitelist-input"
                  placeholder="example.com, client.com"
                  required
                  handler={handleWhiteListChange}
                />
                <FormHelperText>{DICTIONARY.UseCommas}</FormHelperText>
              </FormControl>
            </IconRowCell>
          </IconRow>

          {/* Primary contact should be managed here only when creating. */}
          {!isEdit && (
            <IconRow isInput>
              <IconRowCell type="icon">
                <EmailIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <FormControl required sx={{ marginBottom: '1rem', width: '100%' }}>
                  {!emailIsInWhitelist(
                    emailInput,
                    companyInput.domainWhitelist || [],
                  )
                    || emailInput.trim() === ''
                    || !primaryContacts[0].employer ? (
                      <TextField
                        sx={{ flexGrow: 1, width: '100%' }}
                        label="Enter email"
                        id="primary-contact-input-0"
                        name="primary-contact"
                        onChange={handlePrimaryContactInput}
                        error={
                          primaryContacts[0]
                          && typeof primaryContacts[0].error === 'string'
                        }
                        value={primaryContacts[0].email}
                        helperText={primaryContacts[0].error}
                        required
                        inputProps={{
                          'data-testid': 'primary-contact-input-0',
                        }}
                      />
                    ) : (
                      <Box sx={(t) => ({ borderBottom: `2px solid ${t.palette.primary1}`, width: '100%' })}>
                        <InputLabel required shrink>Enter email</InputLabel>
                        <Chip
                          avatar={(
                            <S3Avatar
                              alt={primaryContacts[0].email}
                              picture={primaryContacts[0].employer?.avatar}
                            />
                          )}
                          sx={(t) => ({
                            boxShadow: t.shadows[3],
                            marginBottom: '0.5rem',
                            marginTop: '1.5rem',
                          })}
                          label={primaryContacts[0].employer?.lastname
                            ? fullname(primaryContacts[0].employer)
                            : primaryContacts[0].email}
                          onDelete={handleDelete}
                        />
                      </Box>
                    )}
                </FormControl>
                <Toggle
                  label="Send invitation:"
                  value={
                    primaryContacts[0].employer?.username ? false : sendInvite
                  }
                  handleChange={() => setSendInvite(!sendInvite)}
                  disabled={Boolean(primaryContacts[0].employer?.username)}
                />
              </IconRowCell>
            </IconRow>
          )}
        </Box>
      )}

      {canSeeCompanyPreferences && (
        <>
          <IconRow isHeader>
            <IconRowCell type="icon">
              <IconCircle Icon={BusinessIcon} />
            </IconRowCell>
            <IconRowCell type="text">
              <Typography component="h3" variant="h5">
              Company Preferences
              </Typography>
            </IconRowCell>
          </IconRow>

          <IconRow>
            <IconRowCell type="icon" alignSelf="center">
              <FlagIcon fontSize="small" />
            </IconRowCell>
            <IconRowCell type="text">
              <Toggle
                disabled={companyInput.isPartnerCompany || false}
                label="Parent company:"
                value={
                  companyInput.isPartnerCompany
                    ? false
                    : companyInput.isParent || false
                }
                handleChange={(value: boolean) => setCompanyInput({
                  ...companyInput,
                  isParent: value,
                })}
              />
            </IconRowCell>
          </IconRow>

          {companyInput.isParent && !companyInput.isPartnerCompany && (
            <IconRow isInput>
              <IconRowCell type="icon" sx={{ mt: 1 }}>
                <BusinessIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <InputLabel htmlFor="company-subsidiaries" shrink>
                Subsidiaries
                </InputLabel>
                <AutocompleteWithSearch
                  multiple
                  type="Company"
                  elementId="company-subsidiaries"
                  className="autocomplete--chips"
                  ignoreEnter
                  // A company cannot be its own subsidiary. Guarded on isEdit so
                  // that in create mode no unrelated company is excluded.
                  excludedIDs={isEdit && company?.id ? [company.id] : []}
                  optionFilter={isEligibleSubsidiary}
                  // isEligibleSubsidiary rejects most companies, and it runs
                  // client-side — on 20 rows it could leave a near-empty list
                  // while an eligible company sat just past the cut-off, with
                  // no way for the user to reach it.
                  searchLimit={100}
                  // Carried over from the previous field, which hid this control.
                  // Per-chip delete and Backspace still remove subsidiaries; this
                  // only drops the one-click wipe-everything affordance.
                  disableClearable
                  placeholder={
                    selectedSubsidiaries.length
                      ? 'More'
                      : 'Choose subsidiary company'
                  }
                  onChange={handleSubsidiaryChange}
                  value={selectedSubsidiaries}
                />
              </IconRowCell>
            </IconRow>
          )}

          {isEdit && (
            <IconRow>
              <IconRowCell type="icon" alignSelf="center">
                <ArchiveIcon fontSize="small" />
              </IconRowCell>
              <IconRowCell type="text">
                <Toggle
                  label="Archive company:"
                  value={companyInput.archived === ArchivedState.ARCHIVED}
                  handleChange={(value: boolean) => setCompanyInput({
                    ...companyInput,
                    archived: value
                      ? ArchivedState.ARCHIVED
                      : ArchivedState.ACTIVE,
                  })}
                />
              </IconRowCell>
            </IconRow>
          )}
        </>
      )}

      <Box sx={{
        alignContent: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '2rem',
        marginTop: '2rem',
      }}
      >
        {!isEdit && (
          <Button type="button" onClick={handleReset} className="form-button">
              Reset
          </Button>
        )}
        <Button
          color="primary"
          variant="contained"
          disabled={disabledButton || isCheckingPrimaryContact}
          type="submit"
          sx={{ marginLeft: 'auto' }}
          id="create-company-submit"
        >
          {isEdit ? DICTIONARY.Edit : DICTIONARY.Create}
        </Button>
      </Box>
    </form>
  );
};

export default CompanyForm;
